import axios from 'axios';
import { queueRequest } from '../utils/requestQueue';
import { getNetworkStatus } from '../utils/capacitorNetwork';
import { getStorageJSON } from '../utils/capacitorStorage';

/**
 * Helper to ensure API base URL always ends with /api
 */
const normalizeApiUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  const trimmed = url.trim().replace(/\/+$/, '');
  return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
};

/**
 * Get API base URL with automatic network detection
 * Priority:
 * 1. Runtime config from Capacitor Preferences (for native apps)
 * 2. REACT_APP_API_URL (full URL, e.g., http://192.168.1.100:8000/api)
 * 3. REACT_APP_SERVER_IP + REACT_APP_SERVER_PORT (IP and port separately)
 * 4. Auto-detect from current hostname (for same-network deployment)
 * 5. Fallback to localhost for development
 */
const getApiBaseUrl = async () => {
  // Option 1: Runtime config from storage (for native apps - can be changed without rebuild)
  try {
    const stored = await getStorageJSON('apiBaseUrl');
    if (stored && stored.url) {
      const url = normalizeApiUrl(stored.url);
      console.log('📡 Using API URL from runtime config:', url);
      return url;
    }
  } catch (e) {
    // Ignore errors, continue to other options
  }

  // Option 2: Full API URL from env (build-time)
  if (process.env.REACT_APP_API_URL) {
    const url = normalizeApiUrl(process.env.REACT_APP_API_URL);
    console.log('📡 Using API URL from REACT_APP_API_URL:', url);
    return url;
  }

  // Option 3: Separate IP and Port
  const serverIP = process.env.REACT_APP_SERVER_IP;
  const serverPort = process.env.REACT_APP_SERVER_PORT || '8000';

  if (serverIP) {
    const url = `https://${serverIP}:${serverPort}/api`;
    console.log('📡 Using API URL from REACT_APP_SERVER_IP:', url);
    return url;
  }

  // Option 4: Auto-detect from current hostname (for same-network deployment)
  // Note: In native Android apps, window.location.hostname might be 'localhost'
  // So this might not work well for native apps
  if (typeof window !== 'undefined' && window.location) {
    const currentHost = window.location.hostname;
    if (currentHost !== 'localhost' && currentHost !== '127.0.0.1' && currentHost !== '') {
      const url = `https://${currentHost}:${serverPort}/api`;
      console.log('📡 Using API URL from hostname detection:', url);
      return url;
    }
  }

  // Option 5: Development fallback
  const baseUrl = `http://localhost:${serverPort}/api`;
  console.warn('⚠️ Using fallback API URL:', baseUrl);
  console.warn('⚠️ To set API URL: Create .env file with REACT_APP_API_URL=https://your-server-domain/api');
  return baseUrl;
};

// Get initial API URL (sync for immediate use, async for native apps)
// For native apps, we'll update it asynchronously
let API_BASE_URL = null;

// Sync version for immediate initialization
const getApiBaseUrlSync = () => {
  // Option 1: Full API URL from env (build-time)
  if (process.env.REACT_APP_API_URL) {
    return normalizeApiUrl(process.env.REACT_APP_API_URL);
  }

  // Option 2: Separate IP and Port
  const serverIP = process.env.REACT_APP_SERVER_IP;
  const serverPort = process.env.REACT_APP_SERVER_PORT || '8000';

  if (serverIP) {
    return `https://${serverIP}:${serverPort}/api`;
  }

  // Option 3: Development fallback
  return `http://localhost:${serverPort}/api`;
};

// Initialize with sync version first
API_BASE_URL = getApiBaseUrlSync();
console.log('📡 Initial API Base URL:', API_BASE_URL);

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    // Ensure browser never serves stale API responses from its own HTTP cache.
    // Service Worker is disabled in browser mode; this covers the native HTTP cache.
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
  },
});

// Initialize API base URL (can be called multiple times safely)
const initApiBaseUrl = async () => {
  try {
    const stored = await getStorageJSON('apiBaseUrl');
    if (stored && stored.url) {
      API_BASE_URL = stored.url;
      api.defaults.baseURL = stored.url;
      console.log('✅ Updated API Base URL from runtime config:', stored.url);
      return stored.url;
    } else {
      // Try async version to get better URL
      const asyncUrl = await getApiBaseUrl();
      if (asyncUrl !== API_BASE_URL) {
        API_BASE_URL = asyncUrl;
        api.defaults.baseURL = asyncUrl;
        console.log('✅ Updated API Base URL:', asyncUrl);
      }
      return asyncUrl;
    }
  } catch (error) {
    console.warn('⚠️ Could not load runtime API config, using build-time URL');
    return API_BASE_URL;
  }
};

// Update baseURL asynchronously if runtime config exists
(async () => {
  await initApiBaseUrl();
})();

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const isTimeSessionMutation = (config) => {
  const method = config?.method?.toLowerCase();
  const url = config?.url || '';
  if (!url.includes('/time_sessions')) {
    return false;
  }
  if (method === 'post' && (url === '/time_sessions/' || url.endsWith('/time_sessions/'))) {
    return true;
  }
  return method === 'post' && /\/time_sessions\/\d+\/stop\/?$/.test(url);
};

// Retry logic for failed requests
const retryRequest = async (error) => {
  const config = error.config;

  // Never retry session start/stop — duplicates rows on partial failures
  if (config?.__noRetry || isTimeSessionMutation(config)) {
    return Promise.reject(error);
  }
  
  // Don't retry if already retried or if it's not a network/server error
  if (!config || config.__retryCount >= MAX_RETRIES) {
    return Promise.reject(error);
  }
  
  // Only retry on network errors or 5xx server errors
  if (!error.response || (error.response.status >= 500 && error.response.status < 600)) {
    config.__retryCount = config.__retryCount || 0;
    config.__retryCount += 1;
    
    // Wait before retrying
    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * config.__retryCount));
    
    return api(config);
  }
  
  return Promise.reject(error);
};

// Request interceptor - Attach operator headers and ensure baseURL
// Increase timeout for slow endpoints like weights
api.interceptors.request.use(
  async (config) => {
    // Ensure baseURL is set
    if (!api.defaults.baseURL) {
      await initApiBaseUrl();
    }

    // Guard against mixed-content blocking: upgrade http to https when the page is served over https
    if (typeof window !== 'undefined' && window.location?.protocol === 'https:' && config.url && config.url.startsWith('http://')) {
      config.url = config.url.replace(/^http:\/\//i, 'https://');
    }
    
    // Log request for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log('🌐 API Request:', config.method?.toUpperCase(), api.defaults.baseURL + config.url);
    }
    // Use Capacitor storage if available, fallback to localStorage
    let stored = null;
    try {
      stored = await getStorageJSON('operatorAuth');
      if (!stored) {
        // Fallback to localStorage for backward compatibility
        const localStorageValue = localStorage.getItem('operatorAuth');
        if (localStorageValue) {
          stored = JSON.parse(localStorageValue);
        }
      }
    } catch (e) {
      // Fallback to localStorage
      try {
        const localStorageValue = localStorage.getItem('operatorAuth');
        if (localStorageValue) {
          stored = JSON.parse(localStorageValue);
        }
      } catch (e2) {
        console.error('Error parsing operator auth:', e2);
      }
    }

    if (stored) {
      try {
        const { operator_id, pin } = stored;
        if (operator_id && pin) {
          config.headers['X-Operator-Id'] = operator_id;
          config.headers['X-Operator-Pin'] = pin;
        }
      } catch (e) {
        console.error('Error parsing operator auth:', e);
      }
    }
    
    // Increase timeout for slow endpoints (weights, photos, lots)
    if (
      config.url &&
      (config.url.includes('/weights/') ||
        config.url.includes('/photos/') ||
        config.url.includes('/lots/') ||
        config.url.includes('/shared_photos/'))
    ) {
      config.timeout = 60000; // 60 seconds for these endpoints
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors and retries
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // Handle network errors (offline or connection issues)
    if (!error.response) {
      console.error('Network error:', error.message);
      
      // Check network status using Capacitor if available
      let isOffline = !navigator.onLine;
      try {
        const networkStatus = await getNetworkStatus();
        isOffline = !networkStatus.connected;
      } catch (e) {
        // Fallback to navigator.onLine
        isOffline = !navigator.onLine;
      }
      
      // If offline and it's a POST/PUT/PATCH request, queue it for retry
      if (
        isOffline &&
        config &&
        ['post', 'put', 'patch'].includes(config.method?.toLowerCase()) &&
        !isTimeSessionMutation(config)
      ) {
        try {
          await queueRequest({
            url: config.url,
            method: config.method,
            data: config.data,
            headers: config.headers,
          });
          
          // Return a special error that can be handled by components
          const queueError = new Error('Request queued for retry when online');
          queueError.isQueued = true;
          queueError.config = config;
          return Promise.reject(queueError);
        } catch (queueError) {
          console.error('Failed to queue request:', queueError);
        }
      }
      
      return retryRequest(error);
    }
    
    // Handle 401 Unauthorized - clear auth and redirect to login
    if (error.response.status === 401) {
      // Clear from both Capacitor storage and localStorage
      try {
        const { removeStorageItem } = await import('../utils/capacitorStorage');
        await removeStorageItem('operatorAuth');
        await removeStorageItem('operatorData');
      } catch (e) {
        // Fallback to localStorage
        localStorage.removeItem('operatorAuth');
        localStorage.removeItem('operatorData');
      }
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
    }
    
    // Handle 500 errors with retry
    if (error.response.status >= 500 && error.response.status < 600) {
      return retryRequest(error);
    }
    
    return Promise.reject(error);
  }
);

// Retry queued requests when coming back online
if (typeof window !== 'undefined') {
  // Clear stale requests on startup (older than 24 hours)
  import('../utils/requestQueue').then(({ clearStaleRequests }) => {
    clearStaleRequests(24).then((cleared) => {
      if (cleared > 0) {
        console.log(`Cleared ${cleared} stale queued requests`);
      }
    });
  });
  
  window.addEventListener('online', async () => {
    console.log('Connection restored, retrying queued requests...');
    try {
      // Clear stale requests before retrying
      const { clearStaleRequests, retryQueuedRequests } = await import('../utils/requestQueue');
      await clearStaleRequests(24);
      
      const results = await retryQueuedRequests(async (requestData) => {
        return api({
          url: requestData.url,
          method: requestData.method,
          data: requestData.data,
          headers: requestData.headers,
        });
      });
      
      if (results.success > 0) {
        console.log(`Successfully retried ${results.success} queued requests`);
        // Dispatch custom event for UI updates
        window.dispatchEvent(new CustomEvent('queuedRequestsRetried', { detail: results }));
      }
      
      if (results.failed > 0) {
        console.warn(`Failed to retry ${results.failed} requests`);
      }
    } catch (error) {
      console.error('Error retrying queued requests:', error);
    }
  });
}

export default api;

